import { generatePath, useRouteLoaderData } from "react-router";
import type { Info as AthleteProfilePageRoute } from "~types/components/pages/+types/AthleteProfilePage";
import type { Info as AthletePreProfilePageRoute } from "~types/components/pages/+types/AthletePreProfilePage";
import type { Info as CollegeProfileRoute } from "~types/components/pages/+types/CollegeProfile";
import type { Info as CollegeSearchRoute } from "~types/components/pages/+types/CollegeSearchData";
import type { Info as RootRoute } from "~types/+types/root";
import type { Info as SportRoute } from "~types/components/pages/+types/SportData";
import type { Info as UserDataRoute } from "~types/components/pages/+types/UserData";
import type { Info as AuthMiddlewareRoute } from "~types/components/pages/+types/AuthMiddleware";
import type { CreateMetaArgs } from "react-router/route-module";
import { index, layout, route, prefix, type RouteConfig } from "@react-router/dev/routes";

export enum RouteIds {
    ATHLETE_PROFILE = "AthleteProfile",
    COLLEGE_SEARCH = "CollegeSearchData",
    COLLEGE_PROFILE = "CollegeProfile",
    PRE_PROFILE = "PreProfile",
    ROOT = "root",
    SPORT = "SportData",
    USER_DATA = "UserData",
    AUTH_MIDDLEWARE = "AuthMiddleware",
}

export interface RouteLoaders {
    [RouteIds.ATHLETE_PROFILE]: AthleteProfilePageRoute;
    [RouteIds.COLLEGE_PROFILE]: CollegeProfileRoute;
    [RouteIds.COLLEGE_SEARCH]: CollegeSearchRoute;
    [RouteIds.PRE_PROFILE]: AthletePreProfilePageRoute;
    [RouteIds.ROOT]: RootRoute;
    [RouteIds.SPORT]: SportRoute;
    [RouteIds.USER_DATA]: UserDataRoute;
    [RouteIds.AUTH_MIDDLEWARE]: AuthMiddlewareRoute;
}

export const RoutePaths: {
    [key in keyof Pick<typeof RouteIds, "COLLEGE_PROFILE" | "COLLEGE_SEARCH">]: string;
} = {
    COLLEGE_PROFILE: "/college-recruiting/colleges/:sportSlug/:collegeID/:collegeSlug",
    COLLEGE_SEARCH: "/college-recruiting/colleges/:sportSlug",
};

export const routes: RouteConfig = [
    // Internal Routes
    route("/api/health", "apis/health.ts"),

    // APIs - TODO: move to /api after we own the plus.imgacademy.com root
    route("/college-recruiting/track", "apis/track.ts"),
    route("/college-recruiting/location-search", "apis/location-search.ts"),
    route("/college-recruiting/highschool-search", "apis/highschool-search.ts"),
    route("/college-recruiting/metadata", "apis/metadata.ts"),

    route("/error", "components/pages/errors/AuthErrorPage.tsx"),

    // Routes With Auth (All rendered pages and some APIs)
    layout("components/pages/AuthMiddleware.ts", { id: "AuthMiddleware" }, [
        // Authentication
        ...prefix("/auth", [
            route("/login", "components/pages/auth/login.ts"),
            route("/logout", "components/pages/auth/logout.ts"),
            route("/callback", "components/pages/auth/callback.ts"),
            route("/error/under13", "components/pages/errors/under13/Auth0Under13ErrorPage.tsx"),
        ]),

        // Internal Routes
        route("/features", "components/pages/Features.tsx"),

        // APIs
        route("/college-recruiting/favorite/college", "apis/favoriteCollege.ts"),

        // Lead Form
        ...prefix("/college-recruiting", [
            ...prefix("/signup", [
                index("components/pages/lead-form/SignUpPage.tsx"),
                route("/athlete", "components/pages/lead-form/AthleteSignUp1.tsx"),
                route("/athlete2", "components/pages/lead-form/AthleteSignUp2.tsx"),
                route("/parent", "components/pages/lead-form/ParentSignUp1.tsx"),
                route("/parent2", "components/pages/lead-form/ParentSignUp2.tsx"),
            ]),
            route("/error/under13", "components/pages/errors/under13/Under13ErrorPage.tsx"),
        ]),

        // College Search
        route(RoutePaths.COLLEGE_SEARCH, "components/pages/SportData.ts", { id: RouteIds.SPORT }, [
            layout("components/pages/CollegeSearchData.tsx", { id: RouteIds.COLLEGE_SEARCH }, [
                index("components/pages/CollegeSearchPage.tsx"),
                route("map", "components/pages/MapSearchPage.tsx"),
            ]),
        ]),

        // Onboarding
        ...prefix("/onboarding", [
            route("/persona", "components/pages/onboarding/persona/OnboardingPersonaPage.tsx", [
                route("athlete", "components/pages/onboarding/persona/AthleteForm.tsx"),
                route("parent", "components/pages/onboarding/persona/ParentForm.tsx"),
            ]),
        ]),

        // Athlete Pre Profile
        route("/profile", "components/pages/AthletePreProfilePage.tsx", [
            // Default nested route renders the splash page
            index("components/pages/ProfileSplashPage.tsx"),
            // Nested route for creating the sport profile
            route("create", "components/pages/CreateSportProfilePage.tsx"),
        ]),

        // Athlete Profile
        route(
            "/profile/:athleteID/:sportID",
            "components/pages/AthleteProfilePage.tsx",
            {
                id: RouteIds.ATHLETE_PROFILE,
            },
            [
                route("intro", "components/pages/modals/EditIntroModal.tsx"),
                route("key-stats", "components/pages/modals/EditKeyStats.tsx"),
                route("videos", "components/pages/modals/Videos.tsx"),
                route("personal-statement", "components/pages/modals/EditPersonalStatement.tsx"),
            ],
        ),

        // Logged In XP
        route(
            "/college-recruiting/colleges/favorites",
            "components/pages/FavoriteCollegesPage.tsx",
        ),

        // College Profile
        route(
            RoutePaths.COLLEGE_PROFILE,
            "components/pages/UserData.ts",
            {
                id: RouteIds.USER_DATA,
            },
            [index("components/pages/CollegeProfile.tsx", { id: RouteIds.COLLEGE_PROFILE })],
        ),

        // Resource Library
        ...prefix("/resources", [
            index("components/pages/ArticlesPage.tsx"),
            route("articles/:articleSlug", "components/pages/Article.tsx"),
        ]),

        // Fallback 404
        route("*", "components/pages/Splat.tsx"),
    ]),
];

export function metaRouteData<K extends keyof RouteLoaders, P extends keyof RouteLoaders>(
    matches: CreateMetaArgs<RouteLoaders[K]>["matches"],
    routeId: P,
): RouteLoaders[P]["loaderData"] {
    const match = matches.filter(m => m !== undefined).find(({ id }) => id === routeId);
    if (!match) {
        throw new Error(`Route loader data not found for "${routeId}"`);
    }
    return match.data as RouteLoaders[P]["loaderData"];
}

export function useRouteData<K extends keyof RouteLoaders>(
    routeId: K,
): RouteLoaders[K]["loaderData"] {
    const data = useRouteLoaderData<RouteLoaders[K]["loaderData"]>(routeId);
    if (data === undefined) {
        throw new Error(`Route loader data not found for "${routeId}"`);
    }
    return data;
}

export function notFoundIfPublicRequest(request: Request) {
    if (request.headers.get("CF-Connecting-IP")) {
        throw new Response(null, { status: 404, statusText: "Not Found" });
    }
}

export const generateArticleRoute = (articleSlug: string) =>
    generatePath("/resources/articles/:articleSlug", { articleSlug });
